import { BaseApiService } from '@/shared/api/BaseApiService';
import { createServiceToken } from '@/app/di';
import type { ApiRequestConfig } from '@/shared/api/types';
import type {
  IInvoiceRepository,
  EnrichedInvoice,
  InvoiceSummary,
  UninvoicedStep,
  InvoiceStats,
  PaginatedResponse,
  CreateInvoiceWithLineItemsInput,
  RecordPaymentInput,
  EditInvoiceContentsInput,
  Payment,
} from '../domain/repositories/invoice.repository';

export type { IInvoiceRepository };
export const INVOICE_SERVICE = createServiceToken<IInvoiceRepository>('INVOICE_SERVICE');

export class InvoiceService extends BaseApiService implements IInvoiceRepository {
  protected readonly resourcePath = 'billing';

  private async getG<T>(path: string, config?: ApiRequestConfig): Promise<T> {
    const { data } = await this.client.get<T>(path, config);
    return data;
  }

  private async postG<T>(path: string, body?: unknown, config?: ApiRequestConfig): Promise<T> {
    const { data } = await this.client.post<T>(path, body, config);
    return data;
  }

  async list(params?: {
    page?: number;
    status?: string;
    search?: string;
    patientId?: string;
  }): Promise<PaginatedResponse<InvoiceSummary>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.status) query.set('status', params.status);
    if (params?.search) query.set('search', params.search);
    if (params?.patientId) query.set('patientId', params.patientId);
    const qs = query.toString();
    return this.getG<PaginatedResponse<InvoiceSummary>>(`/billing${qs ? `?${qs}` : ''}`);
  }

  async getDetail(id: string): Promise<EnrichedInvoice> {
    return this.getG<EnrichedInvoice>(`/billing/${id}/detail`);
  }

  async getUninvoicedSteps(patientId: string, appointmentId?: string): Promise<UninvoicedStep[]> {
    const qs = new URLSearchParams({ patientId });
    if (appointmentId) qs.set('appointmentId', appointmentId);
    return this.getG<UninvoicedStep[]>(`/billing/uninvoiced-steps?${qs.toString()}`);
  }

  async getPayments(invoiceId: string): Promise<Payment[]> {
    return this.getG<Payment[]>(`/billing/${invoiceId}/payments`);
  }

  async getStats(): Promise<InvoiceStats> {
    return this.getG<InvoiceStats>('/billing/stats');
  }

  async createInvoice(input: CreateInvoiceWithLineItemsInput): Promise<InvoiceSummary> {
    return this.postG<InvoiceSummary>('/billing/with-line-items', input);
  }

  async recordPayment(
    id: string,
    input: RecordPaymentInput,
  ): Promise<InvoiceSummary> {
    return this.postG<InvoiceSummary>(`/billing/${id}/payment`, input);
  }

  async cancelPayment(
    invoiceId: string,
    paymentId: string,
  ): Promise<InvoiceSummary> {
    return this.postG<InvoiceSummary>(
      `/billing/${invoiceId}/payments/${paymentId}/cancel`,
    );
  }

  async editInvoiceContents(
    id: string,
    input: EditInvoiceContentsInput,
  ): Promise<InvoiceSummary> {
    // BaseApiService.patch prefixes resourcePath ('billing') — pass a relative path only.
    return this.patch<InvoiceSummary>(`${id}/contents`, input);
  }

  async voidInvoice(id: string): Promise<InvoiceSummary> {
    return this.patch<InvoiceSummary>(`${id}/void`);
  }
}
